import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component } from "preact";
import { apiRequest, ApiError } from "~/lib/api.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import { navigate, search } from "~/util/router.js";
import { object, parse, string } from "~/util/validate.js";
import Banner from "~/component/Banner.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import PasswordRules, { RULES } from "~/component/PasswordRules.js";
import PublicLayout, { FooterPrompt } from "./PublicLayout.js";
import button from "~/style/button.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Forgot.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Forgot.module.css", import.meta.url);
const css = adopt(sheet);

/** @extends {ApiError<"email">} */
class ForgotError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {},
      email: {
        "validation_failed.required": "Email address is required.",
        "validation_failed.email": "Email address is invalid."
      }
    };
  }
}
export class ForgotPassword extends Component {
  sentTo = signal("");
  sendEmail = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      email: string
    }), Object.fromEntries(form.entries()));
    await apiRequest("POST", "/v2/password/forgot", {
      json: {
        email: data.email
      }
    });
    this.sentTo.value = data.email;
    navigate("/forgot/", {
      replace: true
    });
  });
  render() {
    if (this.sentTo.value) {
      return __h(PublicLayout, {
        title: "Forgot your password?"
      }, __h(Banner, {
        icon: __h(Icon, {
          name: "check"
        }),
        kind: "success"
      }, "Password reset email sent!"), __h("p", {}, "Check your email at ", this.sentTo.value, " to reset your password."), __h("a", {
        "class": button.button,
        href: "/login/"
      }, "Log in"));
    }
    const invalid = "invalid" in search.value;
    const error = this.sendEmail.error && ForgotError.from(this.sendEmail.error);
    return __h(PublicLayout, {
      title: "Forgot your password?",
      onSubmit: this.sendEmail.invoke
    }, __h("div", {}, invalid ? __h(Banner, {
      icon: __h(Icon, {
        name: "warning"
      }),
      kind: "warning",
      animate: "shake"
    }, "The reset code has expired.") : null, __h("p", {}, "Enter your email address below to receive a password reset email.")), __h(Field, {
      label: "Email",
      "for": "email",
      error: error?.fields.email?.message
    }, __h("input", {
      "class": input.input,
      id: "email",
      type: "email",
      name: "email",
      required: true
    })), __h("p", {
      "class": css.error
    }, error?.message), __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.sendEmail.loading
    }, "Send email"), __h(FooterPrompt, {}, "Remembered your password? ", __h("a", {
      href: "/login/"
    }, "Log in")));
  }
}
class ResetError extends ApiError {
  static invalid = "This reset password link is either invalid or expired.";

  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        unknown_error: "Couldn't reset password.",
        forbidden: ResetError.invalid,
        not_found: ResetError.invalid,
        invalid_token: ResetError.invalid
      }
    };
  }
}
export class ForgotPasswordReset extends Component {
  password = signal("");
  reset = signal(false);
  resetPassword = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      password: string,
      token: string
    }), Object.fromEntries(form.entries()));
    try {
      await apiRequest("PATCH", "/v2/password/reset", {
        json: {
          new_password: data.password,
          token: data.token
        }
      });
      this.reset.value = true;
    } catch (e) {
      if (!(e instanceof Error)) throw e;
      const apiError = ResetError.from(e);
      const redirectCodes = new Set(["unauthorized", "invalid_token"]);
      if (!redirectCodes.has(apiError.code)) throw apiError;
      navigate("/forgot/?invalid", {
        replace: true
      });
    }
  });

  /** @override */
  componentDidMount() {
    if (!search.value.token) navigate("/forgot/?invalid", {
      replace: true
    });
  }
  render() {
    const apiError = this.resetPassword.error && ResetError.from(this.resetPassword.error);
    if (this.reset.value) {
      return __h(PublicLayout, {
        title: "Forgot your password?"
      }, __h(Banner, {
        icon: __h(Icon, {
          name: "check"
        }),
        kind: "success"
      }, " Password reset! "), __h("a", {
        "class": button.button,
        href: "/login/"
      }, "Log in"));
    }
    return __h(PublicLayout, {
      title: "Reset your password",
      onSubmit: this.resetPassword.invoke
    }, __h("p", {}, "Enter a new password for your Phaze account."), __h(Field, {
      "for": "password",
      label: "Password",
      error: apiError?.fields.new_password?.message,
      validity: {
        /* this is handled by the password rules component */
        patternMismatch: false
      }
    }, __h(PasswordRules, {
      password: this.password.value
    }, __h("input", {
      "class": input.input,
      id: "password",
      type: "password",
      name: "password",
      required: true,
      ...RULES,
      onInput: async (/** @type {{ currentTarget: HTMLInputElement }} */e) => {
        this.password.value = e.currentTarget.value;
      }
    })), __h("input", {
      type: "hidden",
      name: "token",
      value: search.value.token
    })), __h("p", {
      "class": css.error
    }, apiError?.message), __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.resetPassword.loading
    }, "Change password"));
  }
}